<script lang="ts">
	//Hooks
	import { createEventDispatcher, onMount } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import {
		Text,
		Input,
		Modal,
		Container,
		Button,
		ImageGroup,
		type ImageGroupImageType,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { PowerPricesTariff } from '../../../types/PowerPrices';
	import { capFirstChar } from 'src/helpers/utilities';
	import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	const dispatch = createEventDispatcher();

	export let editedTariff: PowerPricesTariff,
		showModal = false,
		waitForResponse = false;

	let errors = { name: false, price: false, bgColor: false };

	const colorValues = Object.values(AvatarColorVariant).slice(0, -13);
	let imgGroupColors: ImageGroupImageType[] = [...Array(10).keys()].map((_, i) => {
		const currentColor = colorValues[i] as ImageGroupImageType['backgroundColor'];

		return {
			backgroundColor: currentColor,
			id: i,
			selected: false,
			type: 'color',
		};
	});

	const onSelectImg = (e) => {
		editedTariff.bgColor = e.detail.backgroundColor;
		errors.bgColor = false;

		let mappedArray = imgGroupColors.map((color, index) => {
			const isSelected = color.backgroundColor === editedTariff?.bgColor;

			return {
				...color,
				selected: isSelected,
			};
		});

		imgGroupColors = [...mappedArray];
	};

	const setBackgroundColors = () => {
		let selectedIndex = -1;

		let mappedArray = imgGroupColors.map((color, index) => {
			const isSelected = color.backgroundColor === editedTariff?.bgColor;

			if (isSelected) selectedIndex = index;

			return {
				...color,
				selected: isSelected,
			};
		});

		const selectedColor = mappedArray[selectedIndex];
		if (selectedColor) {
			mappedArray.splice(selectedIndex, 1);
			imgGroupColors = [selectedColor, ...mappedArray];
		}
	};

	const errorsLabels = {
			name: $format('set_tariff_name', {
				default: 'Please enter a tariff name.',
			}),
			price: $format('set_tariff_price', {
				default: 'Please enter a valid tariff price.',
			}),
			bgColor: $format('set_tariff_color', {
				default: 'Please select tariff color.',
			}),
		},
		save = async () => {
			let error = false;
			if (editedTariff.name == '') {
				errors.name = true;
				error = true;
			}

			if (!editedTariff.bgColor) {
				errors.bgColor = true;
				error = true;
			}
			if (editedTariff.price.toString() == '' || Number(editedTariff.price) <= 0) {
				errors.price = true;
				error = true;
			}
			if (!error) {
				dispatch('finish');
			}
		},
		onClose = () => {
			showModal = false;
		},
		handlePrice = (e) => {
			editedTariff.price = e.target.value;
			errors.price = false;
		},
		handleName = (e) => {
			editedTariff.name = e.target.value;
			errors.name = false;
		};

	onMount(() => {
		setBackgroundColors();
	});
</script>

<Modal
	on:close={onClose}
	{showModal}
	title={editedTariff?.edit
		? $format('edit_tariff', { default: 'Edit Tariff' })
		: $format('add_tariff', { default: 'Add New Tariff' })}
	removeScrollPadding
>
	<svelte:fragment slot="content">
		<div>
			<Container padding="scrollPaddings">
				<Text element="h2" class="mb-s text-capitalize">
					{$format('tariff_name', { default: 'Tariff name' })}
				</Text>
				<Input
					class="width-md-l width-sm-full"
					icon="fa-solid fa-file-invoice-dollar"
					label={$format('tariff_name', { default: 'Tariff name' })}
					type={'text'}
					value={editedTariff.name}
					error={errors.name ? errorsLabels.name : ''}
					on:input={handleName}
					id="input-o5kcfml"
					aria-labelledby="input-o5kcfml"
				/>
				<hr class="mt-l mb-l" />
			</Container>
			<Container padding="scrollPaddings">
				<Text element="h2" class="mb-s">
					{$format('tariff_price_title', {
						default: 'Price for the Tariff',
					})}
				</Text>
				<Input
					class="width-md-l width-sm-full"
					icon="fa-solid fa-coins"
					label={$format('tariff_price', {
						default: 'Tariff price',
					})}
					type={'number'}
					value={editedTariff.price.toString()}
					error={errors.price ? errorsLabels.price : ''}
					on:input={handlePrice}
					id="input-p1fyx9j"
					aria-labelledby="input-p1fyx9j"
				/>
				<hr class="mt-l mb-l" />
			</Container>

			<Container padding="scrollPaddings">
				<Text styleType="h2">
					{$format('label_select', { default: 'Select' })}
					{capFirstChar($format('label_color', { default: 'color' }))}
				</Text>
				<ImageGroup
					on:click={onSelectImg}
					class={classMap({
						'mt-l': true,
						'mb-s': !errors.bgColor, // DO NOT REMOVE COMMENT: Padding references
						'mb-0': errors.bgColor,
					})}
					images={imgGroupColors}
				/>
				{#if errors.bgColor}
					<Text styleType="tiny" class="mt-s mb-sm-m mb-md-l text-danger">
						{errorsLabels.bgColor}
					</Text>
				{/if}
			</Container>
		</div>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			loading={waitForResponse}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={save}
		>
			{$format('button_save', { default: 'Save' })}
			{$format('title_tariff').toLowerCase()}
		</Button>
	</div>
</Modal>
